/*
 * Copyright 2025 coze-dev Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 */

import { type FC, type SyntheticEvent, useState } from 'react';

import classNames from 'classnames';
import { I18n } from '@coze-arch/i18n';
import {
  IconCozEdit,
  IconCozMore,
  IconCozPlus,
  IconCozSideNav,
  IconCozTrashCan,
} from '@coze-arch/coze-design/icons';
import {
  Button,
  EmptyState,
  Input,
  Menu,
  Modal,
  Spin,
  Tooltip,
} from '@coze-arch/coze-design';
import { OpenCodeFrame } from '@coze-agent-ide/abr-opencode-frame';

import { useCodingSessionsPage } from './use-coding-sessions-page';

import styles from './index.module.less';

export const CodingSessionsPage: FC = () => {
  const [menuID, setMenuID] = useState<number | null>(null);
  const [editingID, setEditingID] = useState<number | null>(null);
  const [editingTitle, setEditingTitle] = useState('');
  const [listCollapsed, setListCollapsed] = useState(false);
  const {
    loading,
    creating,
    items,
    activeId,
    frameTarget,
    updatingID,
    deletingID,
    openSession,
    handleCreate,
    handleDelete,
    handleRename,
    handleTitleSuggested,
    handleFrameReady,
    handleFrameError,
  } = useCodingSessionsPage();

  const stopPropagation = (event: SyntheticEvent) => {
    event.stopPropagation();
  };

  const startRename = (id: number, title: string) => {
    setMenuID(null);
    setEditingID(id);
    setEditingTitle(title);
  };

  const cancelRename = () => {
    if (updatingID === editingID) {
      return;
    }
    setEditingID(null);
    setEditingTitle('');
  };

  const saveRename = async (id: number) => {
    const title = editingTitle.trim();
    const current = items.find(item => item.id === id);
    if (!title) {
      return;
    }
    if (!current || title === current.title) {
      cancelRename();
      return;
    }
    if (await handleRename(id, title)) {
      setEditingID(null);
      setEditingTitle('');
    }
  };

  const confirmDelete = (id: number, title: string) => {
    setMenuID(null);
    Modal.confirm({
      title: I18n.t('abr_coding_sessions_delete_confirm_title'),
      content: I18n.t('abr_coding_sessions_delete_confirm_content', { title }),
      okText: I18n.t('abr_coding_sessions_delete_confirm_ok'),
      okButtonColor: 'red',
      cancelText: I18n.t('Cancel'),
      onOk: () => handleDelete(id),
    });
  };

  const toggleListPanel = () => {
    setMenuID(null);
    if (!listCollapsed) {
      cancelRename();
    }
    setListCollapsed(current => !current);
  };

  return (
    <div className={styles.page}>
      <div
        className={classNames(
          styles.body,
          listCollapsed && styles['body_collapsed'],
        )}
      >
        <aside
          className={classNames(
            styles['list-panel'],
            listCollapsed && styles['list-panel_collapsed'],
          )}
        >
          <div className={styles['list-panel-header']}>
            {!listCollapsed ? (
              <>
                <div className={styles['list-heading']}>
                  <span className={styles['list-title']}>
                    {I18n.t('abr_coding_sessions_list_title')}
                  </span>
                  <span className={styles['list-count']}>{items.length}</span>
                </div>
                <div className={styles['list-header-actions']}>
                  <Button
                    type="primary"
                    size="small"
                    icon={<IconCozPlus />}
                    loading={creating}
                    onClick={() => void handleCreate()}
                  >
                    {I18n.t('abr_coding_sessions_create_short')}
                  </Button>
                  <Tooltip
                    content={I18n.t('abr_coding_sessions_collapse_list')}
                  >
                    <Button
                      type="text"
                      className={styles['list-toggle-button']}
                      icon={<IconCozSideNav width="18px" height="18px" />}
                      aria-label={I18n.t(
                        'abr_coding_sessions_collapse_list',
                      )}
                      onClick={toggleListPanel}
                    />
                  </Tooltip>
                </div>
              </>
            ) : (
              <Tooltip content={I18n.t('abr_coding_sessions_expand_list')}>
                <Button
                  type="text"
                  className={classNames(
                    styles['list-toggle-button'],
                    styles['list-toggle-button_expand'],
                  )}
                  icon={<IconCozSideNav width="18px" height="18px" />}
                  aria-label={I18n.t('abr_coding_sessions_expand_list')}
                  onClick={toggleListPanel}
                />
              </Tooltip>
            )}
          </div>

          {!listCollapsed ? (
            <div className={styles['session-list']}>
              {loading ? (
                <div className={styles['list-state']}>
                  <Spin spinning />
                </div>
              ) : items.length === 0 ? (
                <div className={styles['list-state']}>
                  <EmptyState title={I18n.t('abr_coding_sessions_empty')} />
                </div>
              ) : (
                items.map(item => (
                  <div
                    key={item.id}
                    className={classNames(
                      styles['session-item'],
                      activeId === item.id && styles['session-item_active'],
                    )}
                    onClick={() => void openSession(item.id)}
                  >
                    {editingID === item.id ? (
                      <Input
                        autoFocus
                        className={styles['session-title-input']}
                        value={editingTitle}
                        disabled={updatingID === item.id}
                        onClick={stopPropagation}
                        onChange={value => setEditingTitle(value)}
                        onBlur={cancelRename}
                        onKeyDown={event => {
                          event.stopPropagation();
                          if (event.key === 'Enter') {
                            event.preventDefault();
                            void saveRename(item.id);
                          }
                          if (event.key === 'Escape') {
                            event.preventDefault();
                            cancelRename();
                          }
                        }}
                      />
                    ) : (
                      <span className={styles['session-title']}>
                        {item.title}
                      </span>
                    )}
                    <Menu
                      trigger="custom"
                      position="bottomRight"
                      visible={menuID === item.id}
                      onClickOutSide={() => setMenuID(null)}
                      render={
                        <Menu.SubMenu mode="menu">
                          <Menu.Item
                            icon={<IconCozEdit />}
                            onClick={(_, event) => {
                              event.stopPropagation();
                              event.preventDefault();
                              startRename(item.id, item.title);
                            }}
                          >
                            {I18n.t('abr_coding_sessions_rename')}
                          </Menu.Item>
                          <Menu.Item
                            icon={
                              <IconCozTrashCan color="var(--coz-fg-hglt-red)" />
                            }
                            onClick={(_, event) => {
                              event.stopPropagation();
                              event.preventDefault();
                              confirmDelete(item.id, item.title);
                            }}
                          >
                            <span style={{ color: 'var(--coz-fg-hglt-red)' }}>
                              {I18n.t('abr_coding_sessions_delete')}
                            </span>
                          </Menu.Item>
                        </Menu.SubMenu>
                      }
                    >
                      <Button
                        type="text"
                        className={styles['session-menu-button']}
                        disabled={
                          deletingID === item.id || updatingID === item.id
                        }
                        icon={
                          <IconCozMore color="var(--coz-fg-secondary, #6b7280)" />
                        }
                        onClick={event => {
                          event.stopPropagation();
                          setMenuID(current =>
                            current === item.id ? null : item.id,
                          );
                        }}
                      />
                    </Menu>
                  </div>
                ))
              )}
            </div>
          ) : null}
        </aside>

        <section className={styles['frame-panel']}>
          {frameTarget ? (
            <OpenCodeFrame
              className={styles['opencode-frame']}
              embedUrl={frameTarget.embedUrl}
              opencodeSessionID={frameTarget.opencodeSessionID}
              title={I18n.t('abr_coding_sessions_title')}
              onSessionReady={handleFrameReady}
              onSessionError={handleFrameError}
              onTitleSuggested={handleTitleSuggested}
            />
          ) : (
            <div className={styles['empty-frame']}>
              {I18n.t('abr_coding_sessions_select_hint')}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};
